import { create } from "zustand";
import { worldBossApi, type WorldBossLeaderboard, type WorldBossStatus } from "../api/worldBoss";
import { useBattleStore } from "./useBattleStore";
import { useGameStore } from "./useGameStore";
import { formatLocalWindowLabel } from "../utils/localSchedule";

type LeaderboardTab = "ALL" | "WARRIOR" | "ARCHER" | "MAGE";

interface WorldBossState {
  status: WorldBossStatus | null;
  leaderboard: WorldBossLeaderboard | null;
  tab: LeaderboardTab;
  loading: boolean;
  attacking: boolean;
  error: string | null;

  pollStatus: () => Promise<void>;
  setTab: (tab: LeaderboardTab) => void;
  refreshLeaderboard: () => Promise<void>;
  heartbeat: () => Promise<void>;
  attack: (instantRevive?: boolean) => Promise<void>;
}

export const useWorldBossStore = create<WorldBossState>((set, get) => ({
  status: null,
  leaderboard: null,
  tab: "ALL",
  loading: false,
  attacking: false,
  error: null,

  pollStatus: async () => {
    try {
      const status = await worldBossApi.getStatus();
      set({ status, error: null });
    } catch {
      // Non-fatal background poll
    }
  },

  setTab: (tab) => {
    set({ tab });
    void get().refreshLeaderboard();
  },

  refreshLeaderboard: async () => {
    const { tab, status } = get();
    if (!status?.is_active) return;
    try {
      const leaderboard = await worldBossApi.getLeaderboard(tab);
      set({ leaderboard });
    } catch {
      // Ignore transient errors
    }
  },

  heartbeat: async () => {
    const { status } = get();
    if (!status?.is_active) return;
    try {
      await worldBossApi.presence();
    } catch {
      // Ignore
    }
  },

  attack: async (instantRevive = false) => {
    set({ attacking: true, error: null });
    try {
      const res = await worldBossApi.attack(instantRevive);
      await useBattleStore.getState().loadBattle(res.battle_id, res.info_messages);
      useGameStore.getState().setScreen("battle");
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to attack World Boss" });
    } finally {
      set({ attacking: false });
    }
  },
}));

export function formatBossWindowLabel(status: WorldBossStatus | null): string {
  if (!status) return "World Boss";
  const { utc_start_hour, duration_minutes } = status.window;
  return formatLocalWindowLabel(utc_start_hour, duration_minutes);
}
